import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Block, BlockDoc, BlockType, EditorConfig } from "./types";
import { BlockPalette } from "./BlockPalette";
import { BlockTypeProvider } from "./context";
import {
  duplicateBlock,
  ensureDoc,
  insertBlock,
  moveBlock,
  removeBlock,
  sideInsertBlock,
  updateBlock,
} from "./utils";

/**
 * Generic block-based editor. Vertical list of blocks; each block is sortable
 * via drag handle (left rail). Insertion points appear between blocks (and at
 * the top / bottom) — clicking one opens the palette popover. Click anywhere
 * inside a block to focus and edit it; click outside to commit.
 *
 * The editor is presentational only — caller owns the doc state and persistence.
 */

interface Props {
  doc: BlockDoc | unknown;
  onChange: (next: BlockDoc) => void;
  config: EditorConfig;
  /** Show the editor chrome — drag handles, insertion buttons. False = read-only. */
  editable?: boolean;
  /** Optional placeholder shown when the doc is empty. */
  placeholder?: string;
}

export function BlockEditor({ doc, onChange, config, editable = true, placeholder }: Props) {
  const safeDoc = useMemo(() => ensureDoc(doc), [doc]);
  const types = useMemo(
    () => new Map(config.blockTypes.map((t) => [t.type, t])),
    [config.blockTypes],
  );

  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [paletteAt, setPaletteAt] = useState<number | null>(null);
  /** When the user clicks "+ left" or "+ right" beside a block, we open the
   *  same palette but record the target block id and side so the picker
   *  knows to wrap the existing block instead of inserting between blocks. */
  const [sideInsertAt, setSideInsertAt] = useState<{ blockId: string; side: "left" | "right" } | null>(null);

  // Click-outside to defocus
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!focusedId) return;
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (!root.contains(e.target as Node)) setFocusedId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [focusedId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragStart = useCallback((_e: DragStartEvent) => {
    setFocusedId(null);
  }, []);

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const fromId = String(active.id);
      const toId = String(over.id);
      // arrayMove via our helper
      onChange(moveBlock(safeDoc, fromId, toId));
    },
    [safeDoc, onChange],
  );

  // Avoid TS unused warning for arrayMove (kept for future use cases)
  void arrayMove;

  const handleInsert = (type: BlockType) => {
    if (sideInsertAt) {
      const { doc: nextDoc, id } = sideInsertBlock(safeDoc, sideInsertAt.blockId, type, sideInsertAt.side);
      onChange(nextDoc);
      setSideInsertAt(null);
      setFocusedId(id);
      return;
    }
    if (paletteAt === null) return;
    const { doc: nextDoc, id } = insertBlock(safeDoc, paletteAt, type);
    onChange(nextDoc);
    setPaletteAt(null);
    setFocusedId(id);
  };

  const closePalette = () => {
    setPaletteAt(null);
    setSideInsertAt(null);
  };

  const handleUpdate = (id: string, data: unknown) => onChange(updateBlock(safeDoc, id, data));
  const handleDelete = (id: string) => {
    onChange(removeBlock(safeDoc, id));
    if (focusedId === id) setFocusedId(null);
  };
  const handleDuplicate = (id: string) => onChange(duplicateBlock(safeDoc, id));

  const isEmpty = safeDoc.blocks.length === 0;

  return (
    <BlockTypeProvider config={config}>
    <div ref={rootRef} className={`block-editor ${editable ? "is-editable" : "is-readonly"}`}>
      {editable && (
        <InsertSlot index={0} onClick={() => setPaletteAt(0)} active={paletteAt === 0} />
      )}

      {isEmpty && editable && (
        <div className="block-editor-empty">
          <p className="font-grotesk text-[0.92rem] text-[var(--color-ink-soft)]">
            {placeholder ?? "Empty draft. Add a block to begin."}
          </p>
          <button type="button" className="btn-app-ghost mt-3" onClick={() => setPaletteAt(0)}>
            Add a block
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={safeDoc.blocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          {safeDoc.blocks.map((block, idx) => {
            const t = types.get(block.type);
            return (
              <div key={block.id}>
                <SortableBlock
                  block={block}
                  type={t}
                  editable={editable}
                  isFocused={focusedId === block.id}
                  onFocus={() => setFocusedId(block.id)}
                  onCommit={() => setFocusedId(null)}
                  onChange={(data) => handleUpdate(block.id, data)}
                  onDelete={() => handleDelete(block.id)}
                  onDuplicate={() => handleDuplicate(block.id)}
                  onSideInsert={(side) => setSideInsertAt({ blockId: block.id, side })}
                  sideInsertActive={sideInsertAt?.blockId === block.id ? sideInsertAt.side : null}
                />
                {editable && (
                  <InsertSlot
                    index={idx + 1}
                    onClick={() => setPaletteAt(idx + 1)}
                    active={paletteAt === idx + 1}
                  />
                )}
              </div>
            );
          })}
        </SortableContext>
      </DndContext>

      {(paletteAt !== null || sideInsertAt !== null) && editable && (
        <BlockPalette
          config={config}
          onPick={handleInsert}
          onClose={closePalette}
        />
      )}
    </div>
    </BlockTypeProvider>
  );
}

// -----------------------------------------------------------------------------
// Sortable wrapper around a single block
// -----------------------------------------------------------------------------

function SortableBlock({
  block,
  type,
  editable,
  isFocused,
  onFocus,
  onCommit,
  onChange,
  onDelete,
  onDuplicate,
  onSideInsert,
  sideInsertActive,
}: {
  block: Block;
  type: BlockType | undefined;
  editable: boolean;
  isFocused: boolean;
  onFocus: () => void;
  onCommit: () => void;
  onChange: (data: unknown) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  /** Open the palette in side-insert mode for this block. */
  onSideInsert: (side: "left" | "right") => void;
  /** When the palette is currently open *for this block* on a particular side,
   *  this is that side; otherwise null. Drives the active-state styling. */
  sideInsertActive: "left" | "right" | null;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  if (!type) {
    return (
      <div className="block-row block-row-error">
        <p className="auth-error">— Unknown block type: {block.type}</p>
      </div>
    );
  }

  const { Render, Edit } = type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`block-row ${isFocused ? "is-focused" : ""} ${isDragging ? "is-dragging" : ""}`}
      onMouseDown={(e) => {
        // If user clicks anywhere inside the block (not the chrome), focus it.
        const target = e.target as HTMLElement;
        if (target.closest(".block-chrome, .block-side-insert")) return;
        onFocus();
      }}
    >
      {editable && (
        <div className="block-chrome block-chrome-left" aria-hidden>
          <button
            ref={setActivatorNodeRef}
            type="button"
            className="block-handle"
            title="Drag to reorder"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <span aria-hidden>⋮⋮</span>
          </button>
        </div>
      )}

      {editable && (
        <button
          type="button"
          className={`block-side-insert block-side-insert-left ${sideInsertActive === "left" ? "is-active" : ""}`}
          onClick={() => onSideInsert("left")}
          aria-label="Insert block to the left"
          title="Insert block to the left"
        >
          <span aria-hidden>+</span>
        </button>
      )}

      <div className="block-content">
        {editable ? (
          // Always mount Edit when editable so clicks land directly on the
          // contentEditable surface. Block-config chrome is hidden via CSS
          // when `.is-focused` is absent (see index.css `.block-row:not(.is-focused) …`).
          <Edit data={block.data} onChange={onChange} isFocused={isFocused} onCommit={onCommit} />
        ) : (
          <Render data={block.data} inEditor={false} />
        )}
      </div>

      {editable && (
        <button
          type="button"
          className={`block-side-insert block-side-insert-right ${sideInsertActive === "right" ? "is-active" : ""}`}
          onClick={() => onSideInsert("right")}
          aria-label="Insert block to the right"
          title="Insert block to the right"
        >
          <span aria-hidden>+</span>
        </button>
      )}

      {editable && (
        <div className="block-chrome block-chrome-right">
          <button
            type="button"
            className="block-action"
            title="Duplicate"
            aria-label="Duplicate"
            onClick={onDuplicate}
          >
            ⎘
          </button>
          <button
            type="button"
            className="block-action block-action-danger"
            title="Delete"
            aria-label="Delete"
            onClick={onDelete}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Insertion slot between blocks
// -----------------------------------------------------------------------------

function InsertSlot({
  index,
  onClick,
  active,
}: {
  index: number;
  onClick: () => void;
  active: boolean;
}) {
  return (
    <div className={`block-insert ${active ? "is-active" : ""}`} data-index={index}>
      <button
        type="button"
        className="block-insert-btn"
        onClick={onClick}
        title="Insert block"
        aria-label="Insert block"
      >
        <span className="block-insert-rule" aria-hidden />
        <span className="block-insert-plus" aria-hidden>
          +
        </span>
        <span className="block-insert-rule" aria-hidden />
      </button>
    </div>
  );
}
